'use client';

import { Children, type ReactNode } from 'react';
import { useStageInsetHost, useStageInsets } from './useStageInsets';

/**
 * The rows above the map, floating over it instead of pushing it down.
 *
 * `CopBottomStack`'s twin, and the last half of slice 7 (#505). Two surfaces
 * share the top of the COP: the dock roster and the manual session strip. In
 * the classic shell each is its own row in the map's flex column, so every one
 * of them appearing or disappearing shortens the map and fires `map.resize()`
 * (`DroneMap.tsx:781`), which re-projects the whole picture.
 *
 * Both of them do that at the worst possible moment. The roster arrives AFTER
 * mount on every single load, so the first thing the COP does with a fresh air
 * picture is move it. The strip appears at the instant somebody takes the
 * sticks - which is the one moment the aircraft on screen must not jump.
 *
 * With these two floated the stage's parent has no conditional in-flow children
 * left at all, which is what `tactical-shell-invariants.test.ts` pins.
 *
 * It does not know what either surface says or does: it is a position, not a
 * policy.
 */

export interface CopTopStackProps {
  /**
   * Whether the stack is currently floating over the map, for the REGISTRY only.
   *
   * Deliberately not a class switch, for the reason `CopBottomStack` states at
   * length: whether the rows float is decided in CSS by the `lg:` variants
   * below, so crossing the breakpoint restyles ONE DOM tree instead of
   * re-parenting the rows into a different one. This flag says the same thing in
   * JS, and its only job is to keep `useStageInsetHost` from claiming an inset
   * below `lg`, where the rows are in the map's flow and cover nothing.
   * Reporting a height there would shrink the measured surface twice, once by
   * the layout and once by the inset, and refuse manual flight that is
   * available.
   */
  floating: boolean;
  children: ReactNode;
}

export function CopTopStack({ floating, children }: CopTopStackProps) {
  // `Children.toArray` drops the `false` each row renders as when its condition
  // is unmet. No docks and no manual session means no rows, and an empty host
  // would still be 12 px of top padding - a phantom inset that shrinks the
  // manual control surface to make room for nothing.
  const hasRows = Children.toArray(children).length > 0;

  const ref = useStageInsetHost<HTMLDivElement>('top', floating && hasRows);

  // What `rowChromeInset` in `page.tsx` used to do for these two rows, now that
  // they are a floating host and can do it themselves.
  //
  // Both rows end in something the operator needs to reach: the roster's paging
  // chevron and dock count, and the manual strip's control cluster, which ends
  // in RELEASE - the key that hands the aircraft back to the RC, sitting under
  // notes that say "RTH, LAND and ABORT still work from here". The side panel is
  // `inset-y-0` at `z-20` and this stack is `z-30`, so without the inset this
  // stack would win and draw those controls OVER the panel rather than beside
  // it. The roster's own `scrollIntoView({ inline: 'nearest' })` correction
  // reads this box too, so narrowing it is also what stops a selected dock's
  // chip counting as visible while it sits behind the panel.
  //
  // Padding rather than a narrower box, matching the bottom stack: the rows are
  // free to use the full visible width, and only the covered part is kept clear.
  const insets = useStageInsets();
  const horizontalPadding = floating
    ? {
        ...(insets.left > 0 ? { paddingLeft: `calc(0.75rem + ${insets.left}px)` } : null),
        ...(insets.right > 0 ? { paddingRight: `calc(0.75rem + ${insets.right}px)` } : null),
      }
    : undefined;

  // ONE tree at every width. `display: contents` below `lg` makes both wrappers
  // generate no box at all, so the rows are the same flex children of the map
  // column they have always been, and crossing the breakpoint restyles this tree
  // rather than swapping it for another one. Returning a bare fragment below
  // `lg` is the obvious way to write this and it is wrong: React re-parents the
  // children when the element at this position changes shape, which unmounts
  // everything in it. `CopDockRoster` holds a scroll position it corrects with
  // `scrollIntoView`, and `ManualFlightBanner` is on screen precisely while
  // somebody is flying by hand. Browser zoom crosses `lg` on a 1280 px window,
  // so this is reachable without touching the window at all.
  return (
    <div
      ref={ref}
      data-testid="cop-top-stack"
      data-floating={floating && hasRows ? 'true' : 'false'}
      // `lg:top-0` with the gap as PADDING, not `top-3`: the measured height
      // then includes the gap, so the inset describes the whole band the stack
      // owns rather than leaving a 12 px sliver of map that the grab halo could
      // reach into and the operator could not use.
      className="contents lg:pointer-events-none lg:absolute lg:inset-x-0 lg:top-0 lg:z-30 lg:flex lg:flex-col lg:px-3 lg:pt-3"
      // Below `lg` this element generates no box, so the padding is inert there
      // and there is no second tree to keep in step.
      style={horizontalPadding}
    >
      <div
        // Inert, down to each row's own wrapper, which opts back in with
        // `pointer-events-auto` in `page.tsx`. Putting it here instead would
        // make the 8 px gap BETWEEN the rows swallow map clicks - and the COP
        // dispatches a GO-TO on the next map click with no dialog, so a
        // swallowed click is a command the operator issued and never sees land.
        className="contents lg:pointer-events-none lg:flex lg:w-full lg:flex-col lg:gap-2"
      >
        {children}
      </div>
    </div>
  );
}
